import { memo, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { pick } from 'lodash';
import { shallow } from 'zustand/shallow';

import { SVGIconType } from '../../../constants/svgIcon';
import {
  getWebPageCustomLink,
  getWebPageLink,
  getWebPageVariant,
  WebPageLink,
  WebPageType,
} from '../../../constants/webPageType';
import { usePopup, useShareData } from '../../../context';
import { useSessionStore } from '../../../stores/SessionStore';
import { useUserStore } from '../../../stores/UserStore';
import { ExitCreateGamePopup, LogoutConfirmPopup } from '../../Popups';
import { AvatarNode } from '../AvatarNode';
import { SvgIcon } from '../SVGIcon';

import '../../../App.css';
import './Header.css';

type MouseEventHandler = (event: React.MouseEvent<HTMLElement>) => void;
type AsyncMouseEventHandler = (
  event: React.MouseEvent<HTMLElement>,
) => Promise<void>;
type ClickHandler = MouseEventHandler | AsyncMouseEventHandler;

interface UseWrapHandlerOptions {
  urlPattern?: string;
  showPopup: (element: React.ReactNode) => void;
  hidePopup: () => void;
  PopupComponent?: React.ComponentType<{
    onClickLeave: () => void;
    onClickCancel: () => void;
  }>;
}

export const useWrapHandler = ({
  urlPattern = '/create/memory-game/',
  showPopup,
  hidePopup,
  PopupComponent = ExitCreateGamePopup,
}: UseWrapHandlerOptions) => {
  const location = useLocation();
  // const navigate = useNavigate();
  return useCallback(
    <T extends ClickHandler>(handler: T) => {
      return (event: React.MouseEvent<HTMLElement>) => {
        const excludedPaths = [
          '/create/memory-game',
          '/create/memory-game/help',
          '/create/memory-game/select-type',
        ];

        const isExcludedPath = excludedPaths.some(
          (path) => location.pathname === path,
        );

        const shouldShowPopup =
          location.pathname.includes(urlPattern) && !isExcludedPath;

        if (shouldShowPopup) {
          showPopup(
            <PopupComponent
              onClickLeave={() => {
                hidePopup();
                void handler(event);
              }}
              onClickCancel={hidePopup}
            />,
          );
          return;
        } else {
          // navigate('/');
          return handler(event);
        }
      };
    },
    [location.pathname, urlPattern, showPopup, PopupComponent, hidePopup],
  );
};

// const { fetchRewardReferral } = useRewardStore.getState();

type HeaderProps = {
  webPageType: WebPageType;
};

export const Header = memo(({ webPageType }: HeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sharedData, setSharedData } = useShareData();
  const { showPopup, hidePopup } = usePopup();

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const headerRightRef = useRef<HTMLDivElement | null>(null);

  const userId = useSessionStore((state) => state.activeUserId);
  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));

  const { name, avatar, profilePicture, isTrialUser } = useUserStore(
    (state) => {
      const profile = state.getProfile(userId ?? '');
      return {
        ...pick(profile, ['name', 'avatar', 'profilePicture', 'isTrialUser']),
      };
    },
    shallow,
  );

  // useEffect(() => {
  //   if (userId) {
  //     fetchRewardReferral({ referralSystem: ReferralSystem.Joyajoy }).catch(
  //       console.error,
  //     );
  //   }
  // }, [userId]);

  // const userRewardReferral = useRewardStore(
  //   (store) => store.getRewardReferral(`${ReferralSystem.Joyajoy}_${userId}`),
  //   shallow,
  // );

  const handleClickLogin = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.Entry));
  }, [navigate, webPageType]);

  // const handleClickHome = useCallback(() => {
  //   if (location.pathname !== '/') {
  //     navigate('/');
  //   }
  // }, [location.pathname, navigate]);

  const handleOpenDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const logout = useSessionStore((state) => state.logout);

  const handleLogout = useCallback(async () => {
    setIsOpen(false);
    const toastId = toast.loading(
      t('Please wait while we are signing you out...'),
    );
    await logout();
    toast.dismiss(toastId);
    if (sharedData && sharedData.searchReferralCode) {
      const pageUrl = window.location.href;

      const updatedUrl = new URL(pageUrl);
      if (sharedData.pathName) {
        updatedUrl.pathname = sharedData.pathName;
      }

      const queryParams = new URLSearchParams();
      if (sharedData.searchReferralCode) {
        queryParams.set('referralCode', sharedData.searchReferralCode);
      }
      updatedUrl.search = queryParams.toString();

      const newUrl =
        updatedUrl.pathname + (updatedUrl.search ? `${updatedUrl.search}` : '');

      navigate(newUrl, { replace: true });
    } else {
      navigate(getWebPageLink(webPageType, WebPageLink.Entry), {
        replace: true,
      });
      setSharedData(undefined);
    }
    // window.location.reload();
  }, [logout, navigate, setSharedData, sharedData, t, webPageType]);

  const logoutConfirm = useCallback(() => {
    showPopup(
      <LogoutConfirmPopup
        onClickLeave={() => {
          hidePopup();
          handleLogout().catch(console.error);
        }}
        onClickCancel={hidePopup}
      />,
    );
  }, [handleLogout, hidePopup, showPopup]);

  const handleTrialLogout = useCallback(() => {
    if (isTrialUser) {
      navigate('/register');
      setIsOpen(false);
    }
  }, [isTrialUser, navigate]);

  const onClickHome = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.Home));
    setIsOpen(false);
  }, [navigate, webPageType]);

  const onClickAIMemoryGames = useCallback(() => {
    if (userAccount?.activeWebPlanTier) {
      navigate('/club/journey');
    } else {
      navigate('/subscription');
    }
    setIsOpen(false);
  }, [navigate, userAccount?.activeWebPlanTier]);

  const onClickPerformanceReport = useCallback(() => {
    navigate(`/club/${userId}/performance-report`);
    setIsOpen(false);
  }, [navigate, userId]);

  const onClickHotClubs = useCallback(() => {
    navigate('/club/hot');
    setIsOpen(false);
  }, [navigate]);

  const onClickWebStore = useCallback(() => {
    navigate('/store');
    setIsOpen(false);
  }, [navigate]);

  const onClickWebStoreRole = useCallback(() => {
    navigate('/store/role');
    setIsOpen(false);
  }, [navigate]);

  const onClickReferrals = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/referral'));
    setIsOpen(false);
  }, [navigate, webPageType]);

  const onClickStoreReferrals = useCallback(() => {
    navigate('/store/referral');
    setIsOpen(false);
  }, [navigate]);

  // const onClickClubMemebershipTierTickets = useCallback(() => {
  //   navigate('/club-membership-tier/tickets');
  //   setIsOpen(false);
  // }, [navigate]);

  const onClickAccountSettings = useCallback(() => {
    navigate(getWebPageCustomLink(webPageType, '/settings/account'));
    setIsOpen(false);
  }, [navigate, webPageType]);

  const onClickWallet = useCallback(() => {
    navigate('/wallet');
    setIsOpen(false);
  }, [navigate]);

  const onClickSubscription = useCallback(() => {
    navigate('/subscription');
    setIsOpen(false);
  }, [navigate]);

  const onClickSignUp = useCallback(() => {
    navigate('/register'); // TODO: handle webPageType WEA?
    setIsOpen(false);
  }, [navigate]);

  const domainRoutes = ['', '/wea-member'];

  const notShowUserPartPaths = [
    '/start',
    // '/reset-password',
    // '/referral/:id/join',
    // '/name-card/:id/link',
    // '/name-card/:id/collect',
    // '/name-card/transfer/:id/link',
    // '/name-card/transfer/:id/transferral',
    // '/verify',
    // '/club-redeem/:store/rewardOffer/*',
    '/share/game',
    '/share',
    '/tts-enhancement',
    '/login',
    '/register',
  ];

  const notShowUserPartWithDomains: string[] = [];
  domainRoutes.forEach((domain) => {
    notShowUserPartPaths.forEach((userPart) => {
      notShowUserPartWithDomains.push(domain + userPart);
    });
  });

  const isShowUserParts = !notShowUserPartWithDomains.some((path) =>
    matchPath(path, location.pathname),
  );

  const stickHeaderPaths = ['/smart-parents', '/store/referral', ''];

  const stickHeaderWithDomains: string[] = [];
  domainRoutes.forEach((domain) => {
    stickHeaderPaths.forEach((header) => {
      stickHeaderWithDomains.push(domain + header);
    });
  });
  const isStickyPage = stickHeaderWithDomains.some((path) =>
    matchPath(path, location.pathname),
  );

  // clicking outside of the dropdown menu will close it
  useEffect(() => {
    const handleClickOutsideMenu = (event: MouseEvent) => {
      if (menuRef.current && isOpen) {
        if (
          !menuRef.current.contains(event.target as Node) &&
          !headerRightRef.current?.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutsideMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMenu);
    };
  }, [isOpen]);

  const wrapHandler = useWrapHandler({
    urlPattern: '/create/memory-game',
    showPopup,
    hidePopup,
  });

  const wrappedHandlers = {
    handleClickLogin: wrapHandler(handleClickLogin),
    // handleOpenDropdown: wrapHandler(handleOpenDropdown),
    handleLogout: wrapHandler(logoutConfirm),
    handleTrialLogout: wrapHandler(handleTrialLogout),
    //
    onClickHome: wrapHandler(onClickHome),
    onClickAIMemoryGames: wrapHandler(onClickAIMemoryGames),
    onClickPerformanceReport: wrapHandler(onClickPerformanceReport),
    onClickHotClubs: wrapHandler(onClickHotClubs),
    onClickWebStore: wrapHandler(onClickWebStore),
    onClickWebStoreRole: wrapHandler(onClickWebStoreRole),
    onClickReferrals: wrapHandler(onClickReferrals),
    onClickStoreReferrals: wrapHandler(onClickStoreReferrals),
    onClickAccountSettings: wrapHandler(onClickAccountSettings),
    onClickWallet: wrapHandler(onClickWallet),
    onClickSubscription: wrapHandler(onClickSubscription),
    onClickSignUp: wrapHandler(onClickSignUp),
  };

  const headerItems = [
    {
      name: 'Home',
      icon: SVGIconType.WebHome,
      isAvailable: true,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickHome(event)?.catch(console.error);
      },
    },
    {
      name: 'Smart Parents Program',
      icon: SVGIconType.BookGame,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickAIMemoryGames(event)?.catch(console.error);
      },
    },
    {
      name: 'Performance Report',
      icon: SVGIconType.ScoreAnalysis,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickPerformanceReport(event)?.catch(console.error);
      },
    },
    {
      name: 'HOT Spaces',
      icon: SVGIconType.Classroom,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickHotClubs(event)?.catch(console.error);
      },
    },
    {
      name: 'Web Store',
      icon: SVGIconType.WebStoreBasket,
      isAvailable: true, // TODO: change it to tier2 or above
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickWebStore(event)?.catch(console.error);
      },
    },
    {
      name: 'My Referral Role',
      icon: SVGIconType.ReferralRole,
      isAvailable:
        // userRewardReferral?.role === RewardReferralRole.Partner &&
        // [WebPageType.JOYAJOY].includes(webPageType),
        false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickWebStoreRole(event)?.catch(console.error);
      },
    },
    {
      name: 'Referrals',
      icon: SVGIconType.Referral,
      isAvailable: [WebPageType.WEA].includes(webPageType),
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickReferrals(event)?.catch(console.error);
      },
    },
    {
      name: 'Referrals & Rewards',
      icon: SVGIconType.Referral,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickStoreReferrals(event)?.catch(console.error);
      },
    },
    {
      name: 'Account',
      icon: SVGIconType.Setting,
      isAvailable: true,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickAccountSettings(event)?.catch(console.error);
      },
    },
    {
      name: 'Wallet',
      icon: SVGIconType.Wallet,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickWallet(event)?.catch(console.error);
      },
    },
    {
      name: 'Subscription',
      icon: SVGIconType.Subscription,
      isAvailable: false,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        wrappedHandlers.onClickSubscription(event)?.catch(console.error);
      },
    },
    {
      name: 'Logout',
      icon: SVGIconType.Logout,
      isAvailable: true,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        if (isTrialUser) {
          wrappedHandlers.handleTrialLogout(event)?.catch(console.error);
        } else {
          wrappedHandlers.handleLogout(event)?.catch(console.error);
        }
      },
    },
  ];

  return (
    <header
      className="header"
      style={
        isStickyPage ? { position: 'sticky', top: '0', zIndex: 100 } : undefined // sticky header zindex
      }
    >
      <div className="header-container">
        <div className="left">
          <a
            href={getWebPageLink(webPageType, WebPageLink.Home)}
            target="_self"
          >
            <img
              className="icon"
              src={getWebPageVariant(webPageType).horizontalLogo}
              alt="JOYAJOY Logo"
            />
          </a>
        </div>
        {isShowUserParts && (
          <div>
            {userId ? (
              <div
                className="right"
                ref={headerRightRef}
                onClick={handleOpenDropdown}
              >
                <AvatarNode
                  avatar={{
                    ...avatar,
                    userId,
                    source:
                      profilePicture && profilePicture.publicUrls?.small
                        ? { uri: profilePicture.publicUrls?.small }
                        : '',
                  }}
                  size={40}
                />
                <p className="name">{name}</p>

                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SvgIcon
                    name={SVGIconType.Arrow}
                    color={'#808080'}
                    width={20}
                    hanging={20}
                    className="arrow-icon"
                  />
                </motion.div>
              </div>
            ) : (
              <p
                className="login"
                onClick={(e) => {
                  wrappedHandlers.handleClickLogin(e)?.catch(console.error);
                }}
              >
                {t('Login')}
              </p>
            )}
          </div>
        )}
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            className="drop-down"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {userId && (
              <div className="avatar-name-container">
                <AvatarNode
                  avatar={{
                    ...avatar,
                    userId,
                    source:
                      profilePicture && profilePicture.publicUrls?.small
                        ? { uri: profilePicture.publicUrls?.small }
                        : '',
                  }}
                  size={48}
                />
                <p className="avatar-name">{name}</p>
              </div>
            )}
            {headerItems.map((item) => {
              return item.isAvailable ? (
                <div
                  key={item.name}
                  className="item-container"
                  onClick={item.onClick}
                >
                  <SvgIcon
                    name={item.icon}
                    className={'item-icon'}
                    color={'#333333'}
                  />
                  <p className="item-text">{t(item.name)}</p>
                </div>
              ) : null;
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
});
